import * as React from 'react';

import { NumberField, type NumberFieldState } from '../../atoms/NumberField';
import { cn } from '../../lib/utils';

/**
 * Discriminated CodeInput error variant per Figma `289:49560` and design.md
 * § 3.4.
 *
 * - `wrong-code`: cell has a value but the entered code is invalid (filled cell
 *   with red border + helper "Wrong code").
 * - `empty`: cell is empty but required (empty cell with red border + helper
 *   "Required field"). Useful when the form is submitted before all digits are
 *   filled in.
 *
 * The legacy `error?: boolean` shape is preserved as an optional escape hatch
 * for callers that drive their own helper-text rendering.
 */
export type CodeInputError = { kind: 'wrong-code' | 'empty'; message: string };

export interface CodeInputProps {
  /** Current value of the input (single digit) */
  value: string;
  /** Callback when the value changes */
  onChange: (value: string) => void;
  /** Callback when backspace is pressed on empty input */
  onBackspace?: () => void;
  /** Callback to move focus to next input */
  onNext?: () => void;
  /** Callback when paste is detected */
  onPaste?: (value: string) => void;
  /** Callback when Enter key is pressed */
  onEnter?: () => void;
  /**
   * Whether the input has an error.
   * - `true` / `false` keeps the legacy (Phase < 5) boolean API: red border, no
   *   helper text rendered by this molecule.
   * - Discriminated `{ kind, message }` adds Phase 5 Wrong-Code / Empty variants
   *   per Figma `289:49560` — the molecule renders the helper text in red
   *   below the cell.
   */
  error?: boolean | CodeInputError;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Input ref for focus management */
  inputRef?: React.RefObject<HTMLInputElement | null> | ((el: HTMLInputElement | null) => void);
  /** Additional CSS classes */
  className?: string;
}

/**
 * CodeInput Molecule
 *
 * A single digit input field for verification codes. Composes the
 * `NumberField` atom (single-digit cell) and adds OTP-specific keyboard
 * orchestration (auto-advance, backspace-to-prev, paste handling).
 *
 * @example
 * ```tsx
 * <CodeInput
 *   value={digit}
 *   onChange={setDigit}
 *   onNext={focusNext}
 *   onBackspace={focusPrev}
 *   error={{ kind: 'wrong-code', message: 'Wrong code' }}
 * />
 * ```
 */
export const CodeInput = React.forwardRef<HTMLDivElement, CodeInputProps>(
  (
    {
      value,
      onChange,
      onBackspace,
      onNext,
      onPaste,
      onEnter,
      error = false,
      disabled = false,
      inputRef,
      className,
    },
    ref
  ) => {
    const handleChange = React.useCallback(
      (next: string) => {
        // Pass through atom-level value (already a single digit or empty).
        onChange(next);
        if (next !== '') {
          onNext?.();
        }
      },
      [onChange, onNext]
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && value === '') {
          onBackspace?.();
        } else if (e.key === 'Enter') {
          onEnter?.();
        }
      },
      [value, onBackspace, onEnter]
    );

    const handlePaste = React.useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        const digits = pastedData.replace(/\D/g, '');
        if (digits.length > 0) {
          onPaste?.(digits);
        }
      },
      [onPaste]
    );

    // Truthiness covers both the legacy boolean and the discriminated object.
    const hasError = Boolean(error);
    const errorVariant: CodeInputError | null =
      typeof error === 'object' && error !== null ? error : null;

    // Map molecule-level error/value to atom-level state. For both error
    // variants ('wrong-code' = filled, 'empty' = empty) the cell renders with
    // the red border via NumberField's 'error' state.
    const cellState: NumberFieldState = hasError ? 'error' : value === '' ? 'empty' : 'full';

    return (
      <div ref={ref} className={cn('inline-flex flex-col items-stretch gap-1', className)}>
        <div className="w-12 h-12">
          <NumberField
            value={value}
            onChange={handleChange}
            state={cellState}
            disabled={disabled}
            inputRef={inputRef}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            className="w-full h-full"
          />
        </div>
        {errorVariant ? (
          <p
            data-codeinput-helper={errorVariant.kind}
            className="text-[10px] font-normal leading-[1.2] text-error"
          >
            {errorVariant.message}
          </p>
        ) : null}
      </div>
    );
  }
);

CodeInput.displayName = 'CodeInput';

export default CodeInput;
